/**
 * Theme configuration for MultibotdashboardV7 with an industrial control-room palette.
 */

export type Theme = 'classic' | 'industrial';

const THEME_KEY = 'freqtrade-dashboard-theme';

// Industrial color palette
export const afColors = {
  // Background colors
  bg: {
    primary: '#0b0f12',
    secondary: '#141a1f',
    tertiary: '#1c242b',
  },
  // Text colors
  text: {
    primary: '#e4e7ea',
    secondary: '#99a2aa',
    muted: '#6f7b84',
  },
  // Accent colors
  accent: {
    blue: '#7aa2b8',
    green: '#62a87c',
    red: '#c96b5c',
    yellow: '#c49243',
    purple: '#7b8696',
  },
  // Border colors
  border: {
    default: '#34404a',
    hover: '#7aa2b8',
  },
  // Status colors
  status: {
    healthy: '#62a87c',
    degraded: '#c49243',
    unreachable: '#c96b5c',
    unknown: '#99a2aa',
  },
} as const;

function normalizeTheme(theme: string | null | undefined): Theme {
  if (theme === 'classic' || theme === 'industrial') {
    return theme;
  }
  if (theme === 'dark' || theme === 'light' || theme === 'system') {
    return 'classic';
  }
  return 'industrial';
}

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'industrial';
  return normalizeTheme(localStorage.getItem(THEME_KEY));
}

export function applyTheme(theme: Theme): void {
  const normalizedTheme = normalizeTheme(theme);
  document.documentElement.classList.add('dark');
  document.documentElement.dataset.theme = normalizedTheme;
  localStorage.setItem(THEME_KEY, normalizedTheme);
}

export function getEffectiveTheme(theme: Theme): Theme {
  return normalizeTheme(theme);
}

export function initTheme(): void {
  document.documentElement.classList.add('dark');
  applyTheme(getStoredTheme());
}

export function toggleTheme(): void {
  const currentTheme = getStoredTheme();
  applyTheme(currentTheme === 'classic' ? 'industrial' : 'classic');
}

// Health state colors using AlexFinanceData palette
export const colors = {
  healthy: {
    bg: 'bg-[#161b22] border-[#3fb950]',
    text: 'text-[#3fb950]',
    dot: 'bg-[#3fb950]',
    border: 'border-[#3fb950]',
  },
  degraded: {
    bg: 'bg-[#161b22] border-[#d29922]',
    text: 'text-[#d29922]',
    dot: 'bg-[#d29922]',
    border: 'border-[#d29922]',
  },
  unreachable: {
    bg: 'bg-[#161b22] border-[#f85149]',
    text: 'text-[#f85149]',
    dot: 'bg-[#f85149]',
    border: 'border-[#f85149]',
  },
  unknown: {
    bg: 'bg-[#161b22] border-[#8b949e]',
    text: 'text-[#8b949e]',
    dot: 'bg-[#8b949e]',
    border: 'border-[#8b949e]',
  },

  // Profit colors
  profit: {
    positive: 'text-[#3fb950]',
    negative: 'text-[#f85149]',
    neutral: 'text-[#8b949e]',
  },

  // Environment colors
  docker: 'bg-[#161b22] text-[#58a6ff] border-[#58a6ff]',
  baremetal: 'bg-[#161b22] text-[#a371f7] border-[#a371f7]',
  k8s: 'bg-[#161b22] text-[#3fb950] border-[#3fb950]',
  manual: 'bg-[#161b22] text-[#d29922] border-[#d29922]',
} as const;

export function getHealthColor(state: string) {
  switch (state) {
    case 'healthy':
      return colors.healthy;
    case 'degraded':
      return colors.degraded;
    case 'unreachable':
      return colors.unreachable;
    default:
      return colors.unknown;
  }
}

export function getProfitColor(profit: number): string {
  if (profit > 0) return colors.profit.positive;
  if (profit < 0) return colors.profit.negative;
  return colors.profit.neutral;
}

export function getEnvironmentColor(env: string): string {
  switch (env) {
    case 'docker':
      return colors.docker;
    case 'baremetal':
      return colors.baremetal;
    case 'k8s':
      return colors.k8s;
    case 'manual':
      return colors.manual;
    default:
      return colors.manual;
  }
}

// FinanceData specific colors
export const financeColors = {
  crypto: '#7aa2b8',
  stocks: '#8f9aa6',
  portfolio: '#62a87c',
  news: '#c49243',
  economic: '#b86b4b',
  bybit: '#5fa79a',
} as const;
